// Budget Setup wizard — imports an ISP's real budget (CSV upload/paste), auto-maps columns +
// each line to one of Ready's 7 SF-424A categories, validates federal/match reconciliation,
// and commits to bead_budget_line (which flows to Ready via the proven Project Plan push).
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

const SF424A = ['Administrative', 'Land, structures, rights-of-way', 'Architectural and engineering fees', 'Project inspection fees', 'Construction', 'Equipment', 'Contingencies'];

const usd = (n: any) => (n == null || !isFinite(Number(n)) ? '—' : `$${Math.round(Number(n)).toLocaleString()}`);
const num = (v: any) => Number(String(v ?? '').replace(/[$,\s]/g, '')) || 0;

function suggestCategory(name: string): string {
  const s = (name || '').toLowerCase();
  if (/contingen|reserve/.test(s)) return 'Contingencies';
  if (/olt|ont|electronic|cabinet|equipment|hardware|splitter|optic|material|router|\bnid\b/.test(s)) return 'Equipment';
  if (/engineer|design|as[-\s]?built|survey|architect|\ba&e\b|gis|drafting/.test(s)) return 'Architectural and engineering fees';
  if (/inspect|qa\/?qc|quality|testing|verification/.test(s)) return 'Project inspection fees';
  if (/easement|right[-\s]?of[-\s]?way|\brow\b|pole|make[-\s]?ready|\bland\b/.test(s)) return 'Land, structures, rights-of-way';
  if (/admin|management|reporting|legal|permit|compliance|overhead|program|insurance/.test(s)) return 'Administrative';
  if (/construct|aerial|underground|buried|boring|trench|splic|drop|fiber|conduit|\bosp\b|strand|placement|labor/.test(s)) return 'Construction';
  return 'Construction';
}

// minimal robust CSV parser (handles quoted fields + commas/newlines)
function parseCSV(text: string): string[][] {
  const rows: string[][] = []; let row: string[] = []; let field = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') q = false;
      else field += c;
    } else if (c === '"') q = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(field); field = ''; if (row.some(x => x.trim() !== '')) rows.push(row); row = []; }
    else field += c;
  }
  if (field !== '' || row.length) { row.push(field); if (row.some(x => x.trim() !== '')) rows.push(row); }
  return rows;
}

const FIELD_HINTS: Record<string, string[]> = {
  line_item: ['line item', 'item', 'cost item', 'description', 'name'],
  category: ['category', 'sf-424a', 'sf424a', 'bucket', 'class'],
  total: ['total cost', 'total', 'amount', 'cost', 'budget'],
  federal: ['federal', 'grant funds', 'grant', 'bead', 'federal share'],
  match: ['match funds', 'match', 'matching', 'local share', 'match share'],
};
function autoMap(headers: string[]): Record<string, number> {
  const m: Record<string, number> = {};
  for (const field of Object.keys(FIELD_HINTS)) {
    for (const hint of FIELD_HINTS[field]) {
      const idx = headers.findIndex(h => h.toLowerCase().trim().includes(hint));
      if (idx >= 0 && !Object.values(m).includes(idx)) { m[field] = idx; break; }
    }
  }
  return m;
}

type Line = { line_item: string; sf424a_category: string; total_cost_usd: number; federal_share_usd: number; match_share_usd: number };

export default function BudgetSetupWizard({ projectId, project, onClose }: { projectId: string; project?: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2>(1);
  const [raw, setRaw] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [colMap, setColMap] = useState<Record<string, number>>({});
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const ingest = (text: string) => {
    const rows = parseCSV(text);
    if (!rows.length) return;
    const hdr = rows[0].map(h => h.trim());
    setHeaders(hdr); setColMap(autoMap(hdr)); setDataRows(rows.slice(1)); setRaw(text);
  };
  const onFile = (f: File) => { const r = new FileReader(); r.onload = () => ingest(String(r.result || '')); r.readAsText(f); };

  const buildLines = () => {
    const m = colMap;
    const out: Line[] = dataRows.map(r => {
      const name = m.line_item != null ? (r[m.line_item] || '').trim() : '';
      const total = m.total != null ? num(r[m.total]) : 0;
      const fed = m.federal != null ? num(r[m.federal]) : NaN;
      const mat = m.match != null ? num(r[m.match]) : NaN;
      const federal = isFinite(fed) ? fed : (isFinite(mat) ? total - mat : total);
      const match = isFinite(mat) ? mat : total - federal;
      const cat = m.category != null && SF424A.includes((r[m.category] || '').trim()) ? (r[m.category] || '').trim() : suggestCategory(name);
      return { line_item: name, sf424a_category: cat, total_cost_usd: total, federal_share_usd: federal, match_share_usd: match };
    }).filter(l => l.line_item);
    setLines(out); setStep(2);
  };

  const totals = useMemo(() => lines.reduce((a, l) => ({ total: a.total + l.total_cost_usd, federal: a.federal + l.federal_share_usd, match: a.match + l.match_share_usd }), { total: 0, federal: 0, match: 0 }), [lines]);
  const rowErrors = useMemo(() => lines.map(l => {
    const e: string[] = [];
    if (!l.line_item.trim()) e.push('name');
    if (!SF424A.includes(l.sf424a_category)) e.push('category');
    if (Math.abs(l.federal_share_usd + l.match_share_usd - l.total_cost_usd) > 2) e.push('split');
    return e;
  }), [lines]);
  const hasErrors = rowErrors.some(e => e.length > 0) || !lines.length;
  const awardTotal = Number(project?.total_budget_usd ?? project?.total ?? 0);

  const setLine = (i: number, patch: Partial<Line>) => setLines(ls => ls.map((l, j) => {
    if (j !== i) return l;
    const merged = { ...l, ...patch };
    if (patch.total_cost_usd != null || patch.federal_share_usd != null) merged.match_share_usd = merged.total_cost_usd - merged.federal_share_usd;
    return merged;
  }));

  const commit = async () => {
    setCommitting(true);
    try {
      const r = await api.post(`/bead/projects/${projectId}/budget/import`, { lines, replace: true });
      setResult(r);
      if (r?.ok) { await qc.invalidateQueries({ queryKey: ['bead-budget', projectId] }); await qc.invalidateQueries({ queryKey: ['bead-projects'] }); }
    } catch (e: any) { setResult({ ok: false, error: String(e?.message || e) }); }
    finally { setCommitting(false); }
  };

  const template = 'Line Item,Category,Total,Federal,Match\nProgram Management & Reporting,Administrative,105000,84000,21000\nCompliance & Grant Administration,Administrative,45000,36000,9000\nLegal & Permitting Fees,Administrative,35000,28000,7000\nInsurance & Performance Bonding,Administrative,40000,32000,8000\nEasement & Right-of-Way Acquisition,"Land, structures, rights-of-way",60000,48000,12000\nPole Attachment Fees,"Land, structures, rights-of-way",55000,44000,11000\nMake-Ready (Utility Coordination),"Land, structures, rights-of-way",120000,96000,24000\nDOT / Railroad Crossing Permits,"Land, structures, rights-of-way",25000,20000,5000\nHigh-Level Network Design,Architectural and engineering fees,70000,56000,14000\nLow-Level Engineering & Design,Architectural and engineering fees,130000,104000,26000\nField Survey & Walkout,Architectural and engineering fees,55000,44000,11000\nAs-Built Documentation & GIS,Architectural and engineering fees,40000,32000,8000\nThird-Party Construction Inspection,Project inspection fees,90000,72000,18000\nQA/QC Testing & Verification,Project inspection fees,60000,48000,12000\nAerial Fiber Construction,Construction,620000,496000,124000\nUnderground / Buried Construction,Construction,780000,624000,156000\nFiber Splicing & Termination,Construction,210000,168000,42000\nDrops & Service Connections,Construction,180000,144000,36000\n"Handholes, Vaults & Pedestals",Construction,95000,76000,19000\nOLT & Central Office Electronics,Equipment,180000,144000,36000\nOptical Cabinets & Nodes,Equipment,95000,76000,19000\nONTs & Customer Premise Equipment,Equipment,120000,96000,24000\n"Fiber, Cable & Hardware Materials",Equipment,240000,192000,48000\nConstruction Contingency Reserve,Contingencies,150000,120000,30000\n';
  const dl = () => { const b = new Blob([template], { type: 'text/csv' }); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'budget_template.csv'; a.click(); URL.revokeObjectURL(u); };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg, #fff)', borderRadius: 12, width: 'min(1040px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Budget Setup{project ? ` — ${project.project_name}` : ''}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Import your budget → map each line to an SF-424A category → we validate and file it to the state.</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        <div style={{ padding: 20, overflow: 'auto' }}>
          {/* stepper */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, fontSize: 12 }}>
            {['1 · Import', '2 · Map, review & file'].map((t, i) => (
              <div key={i} style={{ padding: '5px 12px', borderRadius: 20, background: step === i + 1 ? '#0b1220' : '#eef2f7', color: step === i + 1 ? '#fff' : '#64748b', fontWeight: 600 }}>{t}</div>
            ))}
          </div>

          {step === 1 && (
            <div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <label style={{ padding: '8px 14px', background: '#2563eb', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Upload CSV<input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={e => e.target.files && onFile(e.target.files[0])} />
                </label>
                <button onClick={dl} style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Download template</button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>…or paste your budget (CSV, with a header row):</div>
              <textarea value={raw} onChange={e => ingest(e.target.value)} placeholder="Line Item,Category,Total,Federal,Match&#10;Aerial Construction,Construction,480000,384000,96000" style={{ width: '100%', minHeight: 150, fontFamily: 'monospace', fontSize: 12, padding: 10, border: '1px solid var(--border)', borderRadius: 6 }} />
              {headers.length > 0 && (
                <div style={{ marginTop: 14, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Detected {dataRows.length} rows. Confirm which column is which:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
                    {[['line_item', 'Line item *'], ['category', 'Category (optional)'], ['total', 'Total *'], ['federal', 'Federal'], ['match', 'Match']].map(([field, label]) => (
                      <div key={field}>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', marginBottom: 3 }}>{label}</div>
                        <select value={colMap[field] ?? -1} onChange={e => setColMap(m => ({ ...m, [field]: Number(e.target.value) }))} style={{ width: '100%', padding: '5px 6px', fontSize: 12, borderRadius: 5, border: '1px solid var(--border)' }}>
                          <option value={-1}>—</option>
                          {headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>No category column? We'll auto-suggest one per line from its name — you can fix any on the next step.</div>
                </div>
              )}
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button disabled={colMap.line_item == null || colMap.total == null || !dataRows.length} onClick={buildLines}
                  style={{ padding: '9px 18px', background: (colMap.line_item != null && colMap.total != null && dataRows.length) ? '#10b981' : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Next: map &amp; review →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                <Stat label="Lines" value={String(lines.length)} />
                <Stat label="Total" value={usd(totals.total)} />
                <Stat label="Federal" value={usd(totals.federal)} tone="#10b981" />
                <Stat label="Match" value={usd(totals.match)} tone="#64748b" />
                {awardTotal > 0 && <Stat label="Reconcile vs award" value={Math.abs(totals.total - awardTotal) < 2 ? '✓ matches' : `off by ${usd(totals.total - awardTotal)}`} tone={Math.abs(totals.total - awardTotal) < 2 ? '#10b981' : '#f59e0b'} />}
              </div>
              <div style={{ maxHeight: '46vh', overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                    {['Line item', 'SF-424A category', 'Total', 'Federal', 'Match'].map(h => <th key={h} style={{ textAlign: h === 'Line item' || h === 'SF-424A category' ? 'left' : 'right', padding: '8px 10px', fontSize: 10, textTransform: 'uppercase', color: '#64748b', borderBottom: '1px solid var(--border)' }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const err = rowErrors[i];
                      return (
                        <tr key={i} style={{ borderTop: '1px solid #f1f5f9', background: err.length ? '#fff7ed' : '#fff' }}>
                          <td style={{ padding: '4px 8px' }}><input value={l.line_item} onChange={e => setLine(i, { line_item: e.target.value })} style={{ width: '100%', border: 'none', fontSize: 12, background: 'transparent' }} /></td>
                          <td style={{ padding: '4px 8px' }}>
                            <select value={l.sf424a_category} onChange={e => setLine(i, { sf424a_category: e.target.value })} style={{ width: '100%', fontSize: 12, padding: '3px 4px', border: err.includes('category') ? '1px solid #ef4444' : '1px solid var(--border)', borderRadius: 4 }}>
                              {SF424A.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}><input value={l.total_cost_usd} onChange={e => setLine(i, { total_cost_usd: num(e.target.value) })} style={{ width: 90, textAlign: 'right', border: 'none', fontSize: 12, background: 'transparent', fontVariantNumeric: 'tabular-nums' }} /></td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}><input value={l.federal_share_usd} onChange={e => setLine(i, { federal_share_usd: num(e.target.value) })} style={{ width: 90, textAlign: 'right', border: err.includes('split') ? '1px solid #ef4444' : 'none', borderRadius: 4, fontSize: 12, color: '#10b981', background: 'transparent', fontVariantNumeric: 'tabular-nums' }} /></td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{usd(l.match_share_usd)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {result && (
                <div style={{ marginTop: 12, padding: 10, borderRadius: 6, fontSize: 12, background: result.ok ? '#ecfdf5' : '#fef2f2', color: result.ok ? '#065f46' : '#991b1b' }}>
                  {result.ok ? `✓ Imported ${result.inserted} budget lines totalling ${usd(result.totals?.total)}. This now files to Ready via the Project Plan push.` : `Failed: ${result.error || (result.errors && result.errors.map((e: any) => `row ${e.row}: ${e.message}`).join('; '))}`}
                </div>
              )}
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={() => setStep(1)} style={{ padding: '9px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>← Back</button>
                <button disabled={hasErrors || committing || result?.ok} onClick={commit}
                  style={{ padding: '9px 20px', background: (hasErrors || result?.ok) ? '#cbd5e1' : '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: hasErrors ? 'not-allowed' : 'pointer' }}>
                  {committing ? 'Saving…' : result?.ok ? 'Saved ✓' : hasErrors ? 'Fix errors to continue' : `Import ${lines.length} lines`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return <div style={{ display: 'flex', flexDirection: 'column' }}><span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.04em', color: '#94a3b8' }}>{label}</span><span style={{ fontSize: 14, fontWeight: 700, color: tone || 'inherit' }}>{value}</span></div>;
}
